import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Zap, Loader2, Crown } from "lucide-react";

const SOLO_FEATURES = [
  "1 paired host",
  "Unlimited local tasks",
  "Bring your own model auth",
  "Up to 10 projects",
  "7-day task history",
  "Local (sim) deploy",
];
const PRO_FEATURES = [
  "Everything in Solo",
  "Unlimited projects",
  "Unlimited history & checkpoints",
  "Cloud deploy adapters (Cloudflare)",
  "Custom-domain deploy",
  "Higher Maintainer limits",
];

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return null; }
}

export default function Billing() {
  const [me, setMe] = useState(null);
  const [period, setPeriod] = useState("month"); // month | year
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);

  async function loadMe() {
    const { data } = await api.get("/billing/me");
    setMe(data);
  }
  useEffect(() => { loadMe(); }, []);

  // Handle return from Stripe checkout.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    const canceled = params.get("canceled");
    if (canceled) {
      toast.message("Checkout canceled", { description: "No charge was made." });
      window.history.replaceState({}, "", "/billing");
      return;
    }
    if (sid) {
      setChecking(true);
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data } = await api.get(`/billing/status/${sid}`);
          if (data.payment_status === "paid") {
            toast.success("You're on Cadence Pro 🎉");
            await loadMe();
            setChecking(false);
            window.history.replaceState({}, "", "/billing");
            return;
          }
          if (data.status === "expired") {
            toast.error("Checkout session expired.");
            setChecking(false);
            window.history.replaceState({}, "", "/billing");
            return;
          }
        } catch (e) { /* keep polling */ }
        if (tries >= 15) {
          setChecking(false);
          toast.message("Still processing", { description: "Refresh in a moment to see your plan update." });
          window.history.replaceState({}, "", "/billing");
          return;
        }
        setTimeout(poll, 2000);
      };
      poll();
    }
  }, []);

  async function upgrade() {
    setBusy(true);
    try {
      const plan_id = period === "year" ? "pro_yearly" : "pro_monthly";
      const { data } = await api.post("/billing/checkout", { plan_id, origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
      else throw new Error("No checkout URL");
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setBusy(false);
    }
  }

  const isPro = me?.is_pro;
  const proUntil = fmtDate(me?.pro_until);
  const monthly = me?.plans?.pro_monthly?.amount ?? 20;
  const yearly = me?.plans?.pro_yearly?.amount ?? 192;
  const proPrice = period === "year" ? `$${yearly}` : `$${monthly}`;
  const proSuffix = period === "year" ? "/yr" : "/mo";

  return (
    <div className="flex-1 overflow-y-auto cadence-scroll" data-testid="billing-page">
      <div className="max-w-4xl mx-auto px-8 py-10">
        <p className="label-caps mb-2">Plan</p>
        <h1 className="font-heading font-800 text-3xl tracking-tight text-gray-900">Billing</h1>
        <p className="text-gray-500 mt-2 text-[15px]">You only pay for Cadence — your model usage runs on your own provider subscriptions.</p>

        {/* Current status */}
        <div data-testid="billing-status-banner"
          className={`mt-6 rounded-xl border px-5 py-4 flex items-center justify-between ${isPro ? "border-amber-200 bg-amber-50" : "border-gray-200 bg-white"}`}>
          <div className="flex items-center gap-3">
            {isPro ? <Crown className="w-5 h-5 text-amber-500" /> : <Zap className="w-5 h-5 text-gray-400" />}
            <div>
              <div className="font-700 text-sm text-gray-900">{isPro ? "Cadence Pro" : "Solo (free)"}</div>
              <div className="text-xs text-gray-500">
                {isPro ? (proUntil ? `Active until ${proUntil}` : "Active") : "Upgrade to unlock cloud deploy + unlimited projects."}
              </div>
            </div>
          </div>
          {checking && <span className="text-xs text-gray-500 inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Confirming payment…</span>}
        </div>

        {/* Billing period toggle */}
        <div className="mt-8 inline-flex items-center rounded-lg border border-gray-200 p-1 bg-white">
          <button data-testid="billing-period-monthly" onClick={() => setPeriod("month")}
            className={`px-3.5 py-1.5 text-sm rounded-md font-medium transition-colors ${period === "month" ? "bg-gray-900 text-white" : "text-gray-600 hover:text-gray-900"}`}>
            Monthly
          </button>
          <button data-testid="billing-period-yearly" onClick={() => setPeriod("year")}
            className={`px-3.5 py-1.5 text-sm rounded-md font-medium transition-colors inline-flex items-center gap-1.5 ${period === "year" ? "bg-gray-900 text-white" : "text-gray-600 hover:text-gray-900"}`}>
            Yearly <span className={`text-[10px] px-1.5 py-0.5 rounded font-700 ${period === "year" ? "bg-emerald-400/20 text-emerald-200" : "bg-emerald-50 text-emerald-700"}`}>SAVE 20%</span>
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          {/* Solo */}
          <div data-testid="plan-solo" className={`rounded-2xl p-6 border ${isPro ? "border-gray-200" : "border-gray-900"} bg-white`}>
            <div className="flex items-center justify-between">
              <span className="font-heading font-700 text-lg">Solo</span>
              {!isPro && <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md">Current</span>}
            </div>
            <div className="mt-3"><span className="font-heading font-800 text-3xl">$0</span><span className="text-gray-400 text-sm">/mo</span></div>
            <div className="text-sm text-gray-500 mt-1">For getting started</div>
            <ul className="mt-5 space-y-2">
              {SOLO_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-gray-700"><Check className="w-4 h-4 text-emerald-600 shrink-0" /> {f}</li>
              ))}
            </ul>
            <button disabled className="mt-6 w-full rounded-lg px-4 py-2.5 text-sm font-medium bg-gray-100 text-gray-500">
              {isPro ? "Included" : "Current plan"}
            </button>
          </div>

          {/* Pro */}
          <div data-testid="plan-pro" className={`rounded-2xl p-6 border bg-white ${isPro ? "border-amber-300" : "border-gray-200"}`}>
            <div className="flex items-center justify-between">
              <span className="font-heading font-700 text-lg flex items-center gap-2">Pro <Zap className="w-4 h-4 text-amber-500" /></span>
              {period === "year" && <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md">20% off</span>}
            </div>
            <div className="mt-3">
              <span className="font-heading font-800 text-3xl">{proPrice}</span>
              <span className="text-gray-400 text-sm">{proSuffix}</span>
              {period === "year" && <span className="text-xs text-gray-400 ml-2">(${(yearly / 12).toFixed(0)}/mo)</span>}
            </div>
            <div className="text-sm text-gray-500 mt-1">For shipping daily</div>
            <ul className="mt-5 space-y-2">
              {PRO_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-gray-700"><Check className="w-4 h-4 text-emerald-600 shrink-0" /> {f}</li>
              ))}
            </ul>
            <button data-testid="plan-cta-pro" onClick={upgrade} disabled={busy || checking}
              className="mt-6 w-full rounded-lg px-4 py-2.5 text-sm font-medium bg-black text-white hover:bg-gray-900 inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : (isPro ? "Extend Pro" : "Upgrade to Pro")}
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-6">
          Secure checkout by Stripe. Pro is a {period === "year" ? "12-month" : "30-day"} access pass — you'll be reminded before it lapses. Cancel anytime by simply not renewing.
        </p>
      </div>
    </div>
  );
}
